// « Mon mois comptable » (03/11/2026) : une page par mois qui regroupe ce qui
// reste à faire — relevé, factures à valider, à payer, à encaisser, marqueurs
// « à rapprocher », Synthèse TVA et conditions de la déclaration —, chaque point
// avec « Ouvrir → ». Lecture seule : aucune écriture depuis cette page.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const prevMonth = (m) => { const [y, mo] = m.split("-").map(Number); return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, "0")}`; };
const nextMonth = (m) => { const [y, mo] = m.split("-").map(Number); return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`; };
const monthLabel = (m) => { const s = new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); };
const STATUS = { ok: { icon: "✓", color: "#16a34a" }, todo: { icon: "●", color: "#b45309" }, info: { icon: "○", color: "#2563eb" } };

export default function MonMoisComptable({ currentCompanyId, onNavigate }) {
  const [month, setMonth] = useState(() => prevMonth(thisMonth()));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setData(null); setError("");
    api.monthOverview.get(month).then(setData).catch(e => setError(e.message));
  }, [month]);
  useEffect(() => { if (currentCompanyId) load(); }, [load, currentCompanyId]);
  const open = (page) => page && onNavigate && onNavigate(page);
  const openBtn = (page) => page ? <button className="btn" style={{ fontSize: 11.5, padding: "2px 8px" }} onClick={() => open(page)}>Ouvrir →</button> : null;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Mon mois comptable</h1>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="btn" onClick={() => setMonth(prevMonth(month))} aria-label="Mois précédent">◀</button>
          <input className="input" type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} style={{ width: "auto" }} />
          <button className="btn" onClick={() => setMonth(nextMonth(month))} disabled={month >= thisMonth()} aria-label="Mois suivant">▶</button>
          <button className="btn" onClick={load}>↻ Actualiser</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
        Tout ce qui reste à faire pour {monthLabel(month)}, dans l'ordre. Cette page ne modifie rien : chaque « Ouvrir → » mène à l'écran où agir.
      </div>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>{error}</div>}
      {!data && !error && <div style={{ color: "var(--text-muted)" }}>Chargement…</div>}
      {data && (
        <>
          <div className="card" style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
            {data.items.map(it => (
              <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: "1px solid #f3f4f6" }}>
                <span style={{ color: STATUS[it.status]?.color, fontWeight: 700, width: 16, textAlign: "center" }}>{STATUS[it.status]?.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{it.title}{it.count != null ? ` : ${it.count}` : ""}{it.amount ? ` · ${eur(it.amount)}` : ""}</div>
                  {it.detail && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{it.detail}</div>}
                </div>
                {it.status !== "ok" && openBtn(it.page)}
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700 }}>Synthèse TVA de {monthLabel(month)}</div>
              {openBtn(data.vat.page)}
            </div>
            <div style={{ fontSize: 13, marginTop: 6 }}>
              TVA collectée {eur(data.vat.collected)} · TVA déductible {eur(data.vat.deductible)} · {data.vat.credit ? <>crédit de TVA <strong>{eur(data.vat.credit)}</strong></> : <>TVA à payer <strong>{eur(data.vat.to_pay)}</strong></>}
            </div>
            <div style={{ fontSize: 12.5, marginTop: 4, color: data.vat.controls_ok ? "#16a34a" : "#b45309" }}>{data.vat.controls_ok ? "✓ Contrôles de cohérence sans écart" : "● Contrôles de cohérence en écart : voir le détail dans la Synthèse TVA"}</div>
            {data.vat.notices.map(n => <div key={n} style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>⚠ {n}</div>)}
          </div>

          <div className="card">
            <div style={{ fontWeight: 700 }}>Déclaration de TVA</div>
            {data.declaration.status === "déclarée" && <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>✓ Déclarée le {String(data.declaration.filed_on).slice(0, 10).split("-").reverse().join("/")}</div>}
            {data.declaration.status === "mois en cours" && <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>Le mois n'est pas terminé.</div>}
            {data.prerequisites && (
              <div style={{ marginTop: 6, fontSize: 13 }}>
                {data.prerequisites.ready
                  ? <div style={{ color: "#16a34a" }}>✓ Tout est prêt : la déclaration peut être enregistrée dans la Synthèse TVA (« Déclaration déposée »). {openBtn("synthesetva")}</div>
                  : <div style={{ color: "#b91c1c", fontWeight: 600 }}>À terminer avant d'enregistrer la déclaration :</div>}
                {data.prerequisites.blockers.map(b => <div key={b.code} style={{ color: "#b91c1c", marginTop: 4 }}>• {b.message} {openBtn(b.page)}</div>)}
                {data.prerequisites.warnings.length > 0 && <div style={{ color: "#b45309", marginTop: 8, fontWeight: 600 }}>À savoir :</div>}
                {data.prerequisites.warnings.map(w => <div key={w.code} style={{ color: "#b45309", marginTop: 4 }}>• {w.message} {openBtn(w.page)}</div>)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
